'use client';

import { type FocusEvent, useEffect, useState } from 'react';
import Image from 'next/image';
import useEmblaCarousel from 'embla-carousel-react';
import styles from './home-page.module.css';

export interface CarouselCard {
  title: string;
  subtitle: string;
  href: string;
  image: {
    src: string;
    alt: string;
    width: number;
    height: number;
  };
}

interface ServiceCardsCarouselProps {
  cards: CarouselCard[];
  sourceInteraction: 'slick.gallery_active';
  fieldName?: string;
  controlLabel: string;
}

export function ServiceCardsCarousel({
  cards,
  sourceInteraction,
  fieldName = 'serviceCards',
  controlLabel,
}: ServiceCardsCarouselProps) {
  const [emblaRef, emblaApi] = useEmblaCarousel({
    align: 'start',
    dragFree: false,
    loop: true,
  });
  const [isPaused, setIsPaused] = useState(false);
  const [isInteractionPaused, setIsInteractionPaused] = useState(false);
  const [isAutoplayDisabled, setIsAutoplayDisabled] = useState(false);

  useEffect(() => {
    const mediaQuery = window.matchMedia(
      '(prefers-reduced-motion: reduce), (hover: none), (pointer: coarse)',
    );
    const updateAutoplayPreference = () => setIsAutoplayDisabled(mediaQuery.matches);

    updateAutoplayPreference();
    mediaQuery.addEventListener('change', updateAutoplayPreference);

    return () => mediaQuery.removeEventListener('change', updateAutoplayPreference);
  }, []);

  useEffect(() => {
    if (!emblaApi || isAutoplayDisabled || isPaused || isInteractionPaused) return;

    const autoplay = window.setInterval(() => {
      emblaApi.scrollNext();
    }, 3000);

    return () => {
      window.clearInterval(autoplay);
    };
  }, [emblaApi, isAutoplayDisabled, isInteractionPaused, isPaused]);

  const handleBlurCapture = (event: FocusEvent<HTMLDivElement>) => {
    const nextFocusTarget = event.relatedTarget;

    if (!(nextFocusTarget instanceof Node) || !event.currentTarget.contains(nextFocusTarget)) {
      setIsInteractionPaused(false);
    }
  };

  return (
    <div
      className={styles.serviceCarousel}
      data-cms-interaction={sourceInteraction}
      data-carousel-library="embla-carousel-react"
      data-carousel-layout="full-width-bordered-cards"
      data-hover-animation="lift-image-scale"
      aria-label={controlLabel}
      aria-roledescription="carousel"
      onBlurCapture={handleBlurCapture}
      onFocusCapture={() => setIsInteractionPaused(true)}
      onMouseEnter={() => setIsInteractionPaused(true)}
      onMouseLeave={() => setIsInteractionPaused(false)}
      role="region"
    >
      <div className={styles.carouselControls} aria-label={`${controlLabel} carousel controls`}>
        <button
          className={styles.carouselControl}
          type="button"
          onClick={() => emblaApi?.scrollPrev()}
        >
          Previous
        </button>
        <button
          className={styles.carouselControl}
          type="button"
          aria-pressed={isPaused}
          onClick={() => setIsPaused((paused) => !paused)}
        >
          {isPaused ? 'Play' : 'Pause'}
        </button>
        <button
          className={styles.carouselControl}
          type="button"
          onClick={() => emblaApi?.scrollNext()}
        >
          Next
        </button>
      </div>
      <div className={styles.carouselViewport} data-carousel-viewport ref={emblaRef}>
        <div className={styles.carouselTrack}>
          {cards.map((card) => (
            <div className={styles.carouselSlide} key={card.title}>
              <a className={styles.serviceCard} href={card.href} data-cms-field={fieldName}>
                <Image
                  src={card.image.src}
                  alt={card.image.alt}
                  width={card.image.width}
                  height={card.image.height}
                  sizes="(max-width: 900px) 76vw, 20vw"
                  unoptimized={card.image.src.endsWith('.gif')}
                />
                <span className={styles.serviceTitle}>{card.title}</span>
                <span className={styles.serviceSubtitle}>{card.subtitle}</span>
              </a>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
